<template>
  <v-card class="px-8">
    <v-card-title class="h6 px-0">
      {{ title }}
    </v-card-title>

    <p
      v-if="isReplicatingRev"
      class="body-1 blue-grey--text text--darken-1 mb-6"
    >
      {{ $t('rev.replication.replication_dialog_description', { siteName }) }}
    </p>

    <v-card-text class="pa-0 ma-0">
      <div
        class="rev-title__container mt-4"
        data-testid="create-rev-form"
        data-tourid="create-rev-form"
      >
        <v-row>
          <v-col cols="7">
            <div class="d-flex align-start">
              <UserAvatar
                class="mt-3"
                fontSize="10px"
                size="32px"
                :account="account"
              />

              <v-textarea
                v-model="rev.name"
                auto-grow
                autocomplete="off"
                autofocus
                class="mx-4 title-input"
                counter
                data-private
                data-testid="create_rev_modal_name"
                data-tourid="create_rev_modal_name"
                dense
                flat
                rows="4"
                ref="revTitle"
                solo
                :disabled="isLoading"
                :maxlength="titleMaxLength"
                :placeholder="$t('rev.create.write_here_the_title_of_your_rev')"
                @input="findIdeaMatch"
                @blur="registerNameEvent"
                @keydown.ctrl.enter="create"
                @keydown.meta.enter="create"
              >
                <template
                  v-slot:counter
                  :max="titleMaxLength"
                  :value="rev.name.length"
                >
                  <div class="mt-2 d-flex">
                    <v-badge
                      v-if="ideaMatches.length > 0"
                      bordered
                      class="me-2"
                      color="red"
                      dot
                      overlap
                    >
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-icon @click="showIdeaMatchDrawer = true" v-on="on"
                            >mdi-lightbulb-on-outline</v-icon
                          >
                        </template>
                        {{
                          $t('idea_match.found_count_ideasWithCount', {
                            count: ideaMatches.length,
                          })
                        }}
                      </v-tooltip>
                    </v-badge>
                    <div class="d-flex">
                      <p
                        v-if="isFindingMatch"
                        class="ma-0 pa-0 me-5 caption grey--text text--lighten-1 loading-transition"
                      >
                        {{ $t('idea_match.looking_for_coincidences') }}
                      </p>
                      <p
                        class="ma-0 pa-0 caption grey--text text--lighten-1"
                        :max="titleMaxLength"
                        :value="rev.name.length"
                      >
                        {{ rev.name.length }}/{{ titleMaxLength }}
                      </p>
                    </div>
                  </div>
                </template>
              </v-textarea>
            </div>
          </v-col>

          <v-divider vertical />

          <v-col cols="5">
            <p class="body-2 blue-grey--text mb-4">
              {{ $t('common.optional_fields') }}
            </p>

            <v-sheet class="d-flex flex-column" rounded elevation="0">
              <TagsSelector
                class="mb-6"
                data-private
                :siteId="siteId"
                :value="revTags"
                @change="handleTagsChange"
              />

              <CategoriesSelector
                class="mb-6"
                data-private
                :siteIds="[siteId]"
                :value="revCategories"
                @input="handleSelectCategories"
              />

              <SituationTypesSelector
                v-if="isQuickAction"
                data-private
                :value="quickActionSituationTypes"
                @input="handleSituationTypes"
              />
            </v-sheet>
          </v-col>
        </v-row>
      </div>

      <Alert
        v-model="showMatchAlert"
        class="mt-6 mb-0"
        :vuetifyProps="{
          dismissible: true,
          value: showMatchAlert,
          color: 'primary',
          icon: aiFeaturesAreEnabled ? null : 'mdi-lightbulb-on-outline',
        }"
        :text="
          $t('idea_match.found_count_ideasWithCount', {
            count: ideaMatches.length,
          })
        "
        :buttonText="$t('idea_match.view_revs')"
        @buttonClick="showIdeaMatchDrawer = true"
      >
        <template v-slot:customIcon>
          <AIIcon v-if="aiFeaturesAreEnabled" iconSize="15" />
        </template>
      </Alert>
    </v-card-text>

    <v-card-actions class="justify-end ma-0 pb-4 mt-8 px-0">
      <slot
        :create="create"
        :cancel="cancel"
        :isLoading="isLoading"
        :isValid="isValid"
      ></slot>
    </v-card-actions>

    <IdeaMatchDrawer
      v-if="showIdeaMatchDrawer"
      data-tourid="idea_match_drawer"
      data-testid="idea_match_drawer"
      :visible="showIdeaMatchDrawer"
      :revs="ideaMatches"
      @close="showIdeaMatchDrawer = false"
      @visitSuggestion="$emit('visitSuggestion')"
    />
  </v-card>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'

import _ from 'lodash'
import { utils } from 'frontend-js-shared'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import access from '@/lib/access'

import constants from '@/constants'

import UserAvatar from '@/components/UserAvatar.vue'
import Alert from '@/components/Alert.vue'
import IdeaMatchDrawer from '@/containers/IdeaMatchListView/IdeaMatchDrawer.vue'
import AIIcon from '@/components/AiAssistant/AIIcon.vue'
import TagsSelector from '@/components/TagsSelector/TagsSelector.vue'
import CategoriesSelector from '@/components/CategoriesSelector.vue'
import SituationTypesSelector from '@/components/SituationTypesSelector.vue'
import parseRevDataForReplication from './parseRevForReplication.js'

const DELETED_ORIGINAL_REV_MESSAGE =
  'The_rev_you_are_trying_to_replicate_from_does_not_exist_'

const EMPTY_REV = Object.freeze({
  name: '',
  owner: null,
  type: '',
  coach: null,
  organization: {},
  site: {},
})

export default {
  name: 'CreateRevModal',

  mixins: [mixin, trackerMixin],

  components: {
    UserAvatar,
    Alert,
    IdeaMatchDrawer,
    AIIcon,
    TagsSelector,
    SituationTypesSelector,
    CategoriesSelector,
  },

  props: {
    type: {
      type: String,
      required: true,
    },
    challengeId: {
      type: String,
      required: false,
    },
    replicateRevData: {
      type: Object,
      required: false,
      default() {
        return null
      },
    },
  },

  data() {
    return {
      isLoading: false,
      disabledTagIdsSet: null,
      ideaMatches: [],
      isFindingMatch: false,
      openRevsDrawer: false,
      rev: {},
      routes: {
        problemSolving: 'problemSolving',
        improvementIdea: 'improvementIdea',
        quickAction: 'quickAction',
      },
      showIdeaMatchDrawer: false,
      showMatchAlert: false,
    }
  },

  created() {
    if (this.replicateRevData) {
      const clonedRev = _.cloneDeep(this.replicateRevData)
      this.rev = {
        ...clonedRev,
        location: {
          ...clonedRev.location,
          site: utils.getEntityId(this.accountAttrs.site),
        },
      }
    } else {
      this.rev = {
        ...EMPTY_REV,
        location: {
          organization: this.organizationId,
          site: utils.getEntityId(this.accountAttrs.site),
          tags: this.parseTags(this.getAccountEnabledTags()),
        },
        name: window.localStorage.getItem('revTitle') || '',
      }
    }
  },

  watch: {
    replicateRevData(newVal) {
      if (newVal) {
        this.rev = _.cloneDeep(newVal)
      } else {
        this.rev = { ...EMPTY_REV }
      }
    },
  },

  mounted() {
    this.focusTextArea()
    this.fetchTags()
    if (this.rev.name) {
      this.findIdeaMatch()
    }
  },

  destroyed() {
    window.localStorage.removeItem('revTitle')
  },

  computed: {
    ...mapGetters(['organization', 'organizationId', 'account']),

    aiFeaturesAreEnabled() {
      return access.aiFeaturesAreEnabled(this.account, this.organization)
    },

    accountAttrs() {
      return {
        organization: this.organizationId || null,
        site: this.account.site || null,
        tags: this.parseTags(this.account.tags),
      }
    },

    titleMaxLength() {
      return constants[this.type].titleMaxLength
    },

    isReplicatingRev() {
      return Boolean(this.replicateRevData)
    },

    title() {
      const revType = _.snakeCase(this.type)
      const customName =
        this.organization?.customNamings[this.type]?.singular ||
        this.$t(`common.${revType}_one`)

      if (this.isReplicatingRev) {
        return this.$t('rev.create.replicate_new_custom_name', { customName })
      }

      return this.$t('rev.create.create_new_custom_name', { customName })
    },

    routeName() {
      return this.$route?.name ?? null
    },

    isValid() {
      return Boolean(!this.rev?.name?.trim?.())
    },

    revTags() {
      return this.rev?.location?.tags || []
    },

    siteId() {
      return utils.getEntityId(this.account.site) ?? null
    },

    siteName() {
      return this.account.site?.name || ''
    },

    isQuickAction() {
      return this.type === constants.revTypes.quickAction
    },

    revCategories() {
      return this.rev?.categories || []
    },

    quickActionSituationTypes() {
      return this.rev?.beforePhase?.situationTypes || []
    },
  },

  methods: {
    ...mapActions({
      createImprovementIdea: `${constants.revTypes.improvementIdea}/createRev`,
      createProblemSolving: `${constants.revTypes.problemSolving}/create`,
      createQuickAction: `${constants.revTypes.quickAction}/create`,
      getIdeaMatches: `${constants.revTypes.improvementIdea}/getIdeaMatches`,
    }),

    async callCreateRev(handler, payloadFormatter) {
      const { revId } = await handler({
        organizationId: this.organizationId,
        data: payloadFormatter ? payloadFormatter() : this.getRevPayload(),
      })

      if (this.challengeId) {
        await api.challenge.linkChallengeToRev(
          this.organizationId,
          this.type,
          revId,
          this.challengeId
        )
      }
      return revId
    },

    createRev() {
      switch (this.type) {
        case constants.revTypes.improvementIdea:
          return this.callCreateRev(
            this.createImprovementIdea,
            this.getImprovementIdeaPayload
          )
        case constants.revTypes.problemSolving:
          return this.callCreateRev(this.createProblemSolving)
        default:
          return this.callCreateRev(
            this.createQuickAction,
            this.getQuickActionPayload
          )
      }
    },

    getImprovementIdeaPayload() {
      return {
        categories: this.rev.categories ?? [],
        name: this.rev.name,
        owner: this.account._id,
        site: utils.getEntityId(this.accountAttrs.site),
        tags:
          this.rev?.location?.tags ||
          this.parseTags(this.getAccountEnabledTags()),
        type: this.type,
      }
    },

    getQuickActionPayload() {
      return {
        ...this.getRevPayload(),
        beforePhase: {
          situationTypes: this.rev?.beforePhase?.situationTypes || [],
        },
      }
    },

    getRevPayload() {
      return {
        name: this.rev.name,
        categories: this.rev.categories ?? [],
        location: {
          organization: this.organizationId,
          site: utils.getEntityId(this.accountAttrs.site),
          tags:
            this.rev?.location?.tags ??
            this.parseTags(this.getAccountEnabledTags()),
        },
        team: {
          owner: this.account._id,
        },
      }
    },

    getAccountEnabledTags() {
      return (
        this.accountAttrs?.tags?.filter?.(
          id => !this.disabledTagIdsSet?.has?.(id)
        ) ?? []
      )
    },

    parseTags(tags = []) {
      return tags?.map?.(utils.getEntityId) ?? []
    },

    async create() {
      if (this.isReplicatingRev) {
        this.replicateRev()
      } else {
        this.createNewRev()
      }
    },

    async createNewRev() {
      this.isLoading = true
      this.$emit('loading', true)
      this.trackCreate('start', this.type, this.routeName)
      try {
        const revId = await this.createRev()

        this.$emit('created')

        this.isLoading = false
        this.showSuccess(
          this.$t('common.your_rev_has_been_created_successfully')
        )
        this.navigateTo({
          name: this.routes[this.type],
          params: {
            revId,
            organizationId: this.organizationId,
            mode: 'edit',
          },
        })
        this.trackCreate('complete', this.type, this.routeName, this.rev, {
          status: 'success',
        })
        window.localStorage.removeItem('revTitle')
      } catch (error) {
        this.isLoading = false
        this.$emit('loading', false)
        this.showError(error.message)
        this.trackCreate('complete', this.type, this.routeName, this.rev, {
          status: 'fail',
          error: error,
        })
        this.$emit('error')
      }
    },

    async callReplicateRev(handler, formattedRev) {
      const { revId } = await handler({
        organizationId: this.organizationId,
        data: formattedRev,
      })
      return revId
    },

    defaultReplicateData(serializedRev) {
      return {
        status: constants.exploreEntityStatus.open,
        team: {
          owner: utils.getEntityId(this.account),
        },
        location: {
          organization: this.organizationId,
          site: utils.getEntityId(this.accountAttrs.site),
          tags: !_.isEmpty(serializedRev?.location?.tags)
            ? serializedRev?.location?.tags?.map?.(utils.getEntityId) ?? []
            : this.parseTags(this.getAccountEnabledTags()),
        },
        replicatedFromRevId:
          serializedRev?.replicatedFromRevId ?? utils.getEntityId(this.rev),
      }
    },

    async replicateQuickAction(parsedRev) {
      const defaultRevData = this.defaultReplicateData(parsedRev)

      const quickActionPayload = {
        ...parsedRev,
        ...defaultRevData,
      }
      return await this.callReplicateRev(
        this.createQuickAction,
        quickActionPayload
      )
    },

    async replicateProblemSolving(parsedRev) {
      const defaultRevData = this.defaultReplicateData(parsedRev)

      const problemSolvingPayload = {
        ...parsedRev,
        ...defaultRevData,
      }
      return await this.callReplicateRev(
        this.createProblemSolving,
        problemSolvingPayload
      )
    },

    async replicateImprovementIdea(parsedRev) {
      const improvementIdeaPayload = {
        ...parsedRev,
        tags: parsedRev.tags ?? this.parseTags(this.getAccountEnabledTags()),
        status: constants.exploreEntityStatus.open,
        team: {
          owner: utils.getEntityId(this.account),
        },
        site: utils.getEntityId(this.accountAttrs.site),
        organization: this.organizationId,
        replicatedFromRevId:
          parsedRev?.replicatedFromRevId ?? utils.getEntityId(this.rev),
      }

      return await this.callReplicateRev(
        this.createImprovementIdea,
        improvementIdeaPayload
      )
    },

    async handleRevReplicationByType(parsedRev, shouldRetry = true) {
      let newRevId = null

      try {
        switch (this.type) {
          case constants.revTypes.quickAction:
            newRevId = await this.replicateQuickAction(parsedRev)
            break
          case constants.revTypes.problemSolving:
            newRevId = await this.replicateProblemSolving(parsedRev)
            break
          case constants.revTypes.improvementIdea:
            newRevId = await this.replicateImprovementIdea(parsedRev)
            break
          default:
            return newRevId
        }
      } catch (error) {
        if (
          error?.response?.data?.tmessage === DELETED_ORIGINAL_REV_MESSAGE &&
          shouldRetry
        ) {
          return this.handleRevReplicationByType(
            {
              ...parsedRev,
              replicatedFromRevId: this.rev.id, // If the current rev is a replica, there's a chance its original rev was deleted. In that case, we replace this id with the current rev's id.
            },
            false
          )
        }

        throw error
      }

      return newRevId
    },

    async replicateRev() {
      try {
        this.isLoading = true
        this.$emit('loading', true)
        this.trackCreate('start', this.type, this.routeName)

        const parsedRev = parseRevDataForReplication(this.type, this.rev)

        const newRevId = await this.handleRevReplicationByType(parsedRev)

        if (!newRevId) {
          throw new Error('Failed to replicate rev: Unknown rev type')
        }

        this.$emit('created')

        this.isLoading = false
        this.showSuccess(
          this.$t('common.your_rev_has_been_created_successfully')
        )
        this.navigateTo({
          name: this.routes[this.type],
          params: {
            revId: newRevId,
            organizationId: this.organizationId,
            mode: 'edit',
          },
        })
        this.trackCreate('complete', this.type, this.routeName, this.rev, {
          status: 'success',
        })
        window.localStorage.removeItem('revTitle')
      } catch (error) {
        this.isLoading = false
        this.$emit('loading', false)
        this.showError(error.message)
        this.trackCreate('complete', this.type, this.routeName, this.rev, {
          status: 'fail',
          error: error,
        })
        this.$emit('error')
      }
    },

    registerNameEvent() {
      if (!this.rev.name) return
      this.trackCreate('addName', this.type, this.routeName, this.rev, {})
    },

    cancel() {
      this.trackCreate('cancel', this.type, this.routeName, this.rev, {})
      this.rev = { ...EMPTY_REV }
      window.localStorage.removeItem('revTitle')
    },

    focusTextArea() {
      this.$refs?.revTitle?.focus?.()
    },

    async findIdeaMatch() {
      if (this.type !== constants.revTypes.improvementIdea) return

      this.debounceGetIdeaMatches()
    },

    debounceGetIdeaMatches: _.debounce(async function() {
      try {
        window.localStorage.setItem('revTitle', this.rev.name)
        this.isFindingMatch = true
        this.ideaMatches = await this.getIdeaMatches({
          organizationId: this.organizationId,
          rev: {
            name: this.rev.name,
            reviewComments: this.rev.name,
            expandSustain: this.rev.name,
            experimentDone: this.rev.name,
            relateIdea: this.rev.name,
            relateProblem: this.rev.name,
            relateResult: this.rev.name,
          },
        })
        if (this.ideaMatches.length > 0) this.showMatchAlert = true
        else this.showMatchAlert = false
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isFindingMatch = false
      }
    }, 300),

    openIdeaMatchDrawer() {
      this.openRevsDrawer = true
    },

    async fetchTags() {
      const response = await api.tag.getAll(this.organizationId, {
        filters: { isEnabled: false },
        limit: 200,
      })

      this.disabledTagIdsSet = new Set(
        response?.data?.results?.map?.(tag => tag._id) ?? []
      )
    },

    handleTagsChange(tagsData) {
      this.rev = {
        ...this.rev,
        location: {
          ...this.rev.location,
          tags: this.parseTags(tagsData.tags),
        },
      }
    },

    handleSituationTypes(value) {
      this.rev = {
        ...this.rev,
        beforePhase: {
          ...(this.rev?.beforePhase ?? {}),
          situationTypes: value,
        },
      }
    },

    handleSelectCategories(value) {
      this.rev = {
        ...this.rev,
        categories: value,
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.title-input {
  &:after {
    display: none;
  }
}

.rev-title {
  &__container {
    display: flex;
  }
}
</style>
